import React, { useState } from 'react';
import { CsvAnalysis } from '@erp/shared-types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import { Checkbox } from 'src/components/ui/checkbox';
import { Label } from 'src/components/ui/label';
import { Textarea } from 'src/components/ui/textarea';
import { AlertContainer, AlertDescription } from 'src/components/ui/alert';
import {
  CheckCircleIcon,
  WarningIcon,
  XCircleIcon,
  CircleNotchIcon,
} from '@phosphor-icons/react';
import {
  useMigrationAnalysis,
  useMigrateCSVs,
} from 'src/api/rebates/useRebateVersions';
import type { VersionInfo } from 'src/api/rebates/types';

interface CSVMigrationWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  versionGroupId: string;
  currentVersionNumber: number;
  availableSourceVersions: VersionInfo[];
}

function CsvAnalysisRow({
  analysis,
  selected,
  onSelectedChange,
}: {
  analysis: CsvAnalysis;
  selected: boolean;
  onSelectedChange: (selected: boolean) => void;
}) {
  const Icon =
    analysis.status === 'compatible'
      ? CheckCircleIcon
      : analysis.status === 'compatible_with_notice'
        ? WarningIcon
        : XCircleIcon;

  const iconColor =
    analysis.status === 'compatible'
      ? 'text-green-600'
      : analysis.status === 'compatible_with_notice'
        ? 'text-yellow-600'
        : 'text-red-600';

  const bgColor =
    analysis.status === 'compatible'
      ? 'bg-green-50'
      : analysis.status === 'compatible_with_notice'
        ? 'bg-yellow-50'
        : 'bg-red-50';

  return (
    <div className={`border rounded-md p-3 ${bgColor}`}>
      <div className="flex items-start gap-3">
        <div className="pt-1">
          {analysis.canMigrate ? (
            <Checkbox
              id={analysis.invoiceId}
              checked={selected}
              onCheckedChange={onSelectedChange}
            />
          ) : (
            <div className="w-4 h-4" />
          )}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <Icon className={`h-4 w-4 ${iconColor}`} />
            <Label
              htmlFor={analysis.invoiceId}
              className="font-medium cursor-pointer"
            >
              {analysis.invoiceNumber}
            </Label>
            <span className="text-sm text-muted-foreground">
              ({analysis.lineItemCount} line items)
            </span>
          </div>
          {analysis.warnings.length > 0 && (
            <div className="mt-2 text-sm text-yellow-800">
              {analysis.warnings.map((warning, idx) => (
                <div key={idx} className="flex items-start gap-1">
                  <span>⚠</span>
                  <span>{warning}</span>
                </div>
              ))}
            </div>
          )}
          {analysis.blockers.length > 0 && (
            <div className="mt-2 text-sm text-red-800">
              {analysis.blockers.map((blocker, idx) => (
                <div key={idx} className="flex items-start gap-1">
                  <span>✕</span>
                  <span>{blocker}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function CSVMigrationWizard({
  open,
  onOpenChange,
  versionGroupId,
  currentVersionNumber,
  availableSourceVersions,
}: CSVMigrationWizardProps) {
  const [sourceVersionNumber, setSourceVersionNumber] = useState<number | null>(
    null,
  );
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<Set<string>>(
    new Set(),
  );
  const [migrationReason, setMigrationReason] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);

  const { data: analysisReport, isLoading: isAnalyzing } = useMigrationAnalysis(
    versionGroupId,
    currentVersionNumber,
    sourceVersionNumber,
    open && sourceVersionNumber !== null,
  );

  const migrateMutation = useMigrateCSVs(versionGroupId, currentVersionNumber);

  function handleToggleInvoice(invoiceId: string, selected: boolean) {
    const newSelection = new Set(selectedInvoiceIds);
    if (selected) {
      newSelection.add(invoiceId);
    } else {
      newSelection.delete(invoiceId);
    }
    setSelectedInvoiceIds(newSelection);
  }

  function handleSelectAll() {
    if (!analysisReport) return;
    const migratable = analysisReport.csvAnalysis
      .filter((csv) => csv.canMigrate)
      .map((csv) => csv.invoiceId);
    setSelectedInvoiceIds(new Set(migratable));
  }

  function handleDeselectAll() {
    setSelectedInvoiceIds(new Set());
  }

  async function handleMigrate() {
    if (selectedInvoiceIds.size === 0 || !sourceVersionNumber) return;

    await migrateMutation.mutateAsync({
      sourceVersionNumber,
      invoiceIds: Array.from(selectedInvoiceIds),
      migrationReason: migrationReason || undefined,
    });

    // Reset state and close
    setSourceVersionNumber(null);
    setSelectedInvoiceIds(new Set());
    setMigrationReason('');
    setShowConfirmation(false);
    onOpenChange(false);
  }

  const migratableCount =
    analysisReport?.csvAnalysis.filter((csv) => csv.canMigrate).length ?? 0;
  const selectedCount = selectedInvoiceIds.size;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Migrate CSVs from Previous Versions</DialogTitle>
          <DialogDescription>
            Select CSVs from a previous version to migrate to Version{' '}
            {currentVersionNumber}. The system will analyze compatibility and
            show which CSVs can be safely migrated.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Source Version Selector */}
          <div className="space-y-2">
            <Label>Source Version</Label>
            <Select
              value={sourceVersionNumber?.toString() ?? ''}
              onValueChange={(value) => {
                setSourceVersionNumber(Number(value));
                setSelectedInvoiceIds(new Set());
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a version to migrate from" />
              </SelectTrigger>
              <SelectContent>
                {availableSourceVersions.map((version) => (
                  <SelectItem
                    key={version.id}
                    value={String(version.versionNumber)}
                  >
                    Version {version.versionNumber} ({version.status})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Loading State */}
          {isAnalyzing && (
            <div className="flex items-center justify-center py-8">
              <CircleNotchIcon className="h-6 w-6 animate-spin" />
              <span className="ml-2">Analyzing compatibility...</span>
            </div>
          )}

          {/* Analysis Results */}
          {analysisReport && !isAnalyzing && (
            <>
              <AlertContainer>
                <AlertDescription>
                  Found {analysisReport.csvAnalysis.length} CSV(s) in source
                  version. {migratableCount} can be migrated.
                </AlertDescription>
              </AlertContainer>

              {migratableCount > 0 && (
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={handleSelectAll}>
                    Select All Compatible
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleDeselectAll}
                  >
                    Deselect All
                  </Button>
                </div>
              )}

              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {analysisReport.csvAnalysis.map((csv) => (
                  <CsvAnalysisRow
                    key={csv.invoiceId}
                    analysis={csv}
                    selected={selectedInvoiceIds.has(csv.invoiceId)}
                    onSelectedChange={(selected) =>
                      handleToggleInvoice(csv.invoiceId, selected)
                    }
                  />
                ))}
              </div>

              {selectedCount > 0 && (
                <div className="space-y-2">
                  <Label>Migration Reason (Optional)</Label>
                  <Textarea
                    placeholder="Add a note about why these CSVs are being migrated..."
                    value={migrationReason}
                    onChange={(e) => setMigrationReason(e.target.value)}
                    rows={3}
                  />
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={selectedCount === 0 || migrateMutation.isPending}
            onClick={() => setShowConfirmation(true)}
          >
            {migrateMutation.isPending ? (
              <>
                <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />
                Migrating...
              </>
            ) : (
              `Migrate ${selectedCount} CSV${selectedCount !== 1 ? 's' : ''}`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmation} onOpenChange={setShowConfirmation}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm CSV Migration</DialogTitle>
            <DialogDescription>
              You are about to migrate {selectedCount} CSV
              {selectedCount !== 1 ? 's' : ''} from Version{' '}
              {sourceVersionNumber} to Version {currentVersionNumber}. This will
              copy the invoice associations, and calculations will be performed
              independently for each version.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowConfirmation(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleMigrate}
              disabled={migrateMutation.isPending}
            >
              {migrateMutation.isPending ? (
                <>
                  <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />
                  Migrating...
                </>
              ) : (
                'Confirm Migration'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
